import { Controller } from "@hotwired/stimulus";
import { controllers } from "@joemasilotti/bridge-components";
import "./component.scss";

export default class extends Controller {
  static targets = ["mobileToggle", "backButton", "logo"];

  connect() {
    this.pageSections = document.querySelectorAll(
      "body > :is(nav:not([data-controller='navigation']):not([data-controller~='mobile-bottom-nav']), main, footer)"
    );
    this.boundHandleEscapePress = this.handleEscapePress.bind(this);
    this.boundHandleMediaQueryChange = this.handleMediaQueryChange.bind(this);
    this.boundUpdateBackButton = this.updateBackButton.bind(this);
    this.boundMarkInternalNavigation = this.markInternalNavigation.bind(this);
    this.boundHandlePageShow = this.handlePageShow.bind(this);

    // Media query for the desktop breakpoint
    this.desktopMediaQuery = window.matchMedia("(width >= 50em)");
    this.mobileBackButtonMediaQuery = window.matchMedia("(width < 50em)");
    this.mobileLocationNavigationMediaQuery = window.matchMedia("(width <= 50em)");

    this.desktopMediaQuery.addEventListener("change", this.boundHandleMediaQueryChange);
    this.mobileBackButtonMediaQuery.addEventListener("change", this.boundUpdateBackButton);
    this.mobileLocationNavigationMediaQuery.addEventListener("change", this.boundHandleMediaQueryChange);
    window.addEventListener("pageshow", this.boundHandlePageShow);
    document.addEventListener("turbo:before-visit", this.boundMarkInternalNavigation);

    this.initializeBackState();
    this.handleMediaQueryChange(this.desktopMediaQuery);
    this.handleMediaQueryChange(this.mobileLocationNavigationMediaQuery);
    this.updateBackButton();
  }

  disconnect() {
    this.desktopMediaQuery.removeEventListener("change", this.boundHandleMediaQueryChange);
    this.mobileBackButtonMediaQuery.removeEventListener("change", this.boundUpdateBackButton);
    this.mobileLocationNavigationMediaQuery.removeEventListener("change", this.boundHandleMediaQueryChange);
    window.removeEventListener("pageshow", this.boundHandlePageShow);
    document.removeEventListener("turbo:before-visit", this.boundMarkInternalNavigation);
  }

  // Handles media query changes
  handleMediaQueryChange(event) {
    if (this.desktopMediaQuery.matches || this.mobileLocationNavigationMediaQuery.matches) {
      if (this.mobileToggleTarget.getAttribute("aria-expanded") === "true") {
        this.mobileToggleTarget.setAttribute("aria-expanded", "false");
      }
      this.enablePageFocus();
      this.enablePageScroll();
      this.enableScrollbarGutter();
      window.removeEventListener("keydown", this.boundHandleEscapePress);
    }
  }

  toggle() {
    const isOpen =
      this.mobileToggleTarget.getAttribute("aria-expanded") === "true";

    this.mobileToggleTarget.setAttribute(
      "aria-expanded",
      isOpen ? "false" : "true"
    );

    if (isOpen) {
      this.enablePageFocus();
      this.enablePageScroll();
      this.enableScrollbarGutter();
      window.removeEventListener("keydown", this.boundHandleEscapePress);
    } else {
      if (!this.desktopMediaQuery.matches) {
        this.disablePageFocus();
        this.disablePageScroll();
        this.disableScrollbarGutter();
        window.addEventListener("keydown", this.boundHandleEscapePress);
      }
    }
  }

  handleEscapePress(e) {
    if (e.key === "Escape") {
      this.mobileToggleTarget.setAttribute("aria-expanded", "false");
      this.enablePageFocus();
      this.enablePageScroll();
      this.enableScrollbarGutter();
      window.removeEventListener("keydown", this.boundHandleEscapePress);
    }
  }

  disablePageFocus() {
    this.pageSections.forEach((section) => section.setAttribute("inert", ""));
  }

  enablePageFocus() {
    this.pageSections.forEach((section) => section.removeAttribute("inert"));
  }

  disablePageScroll() {
    document.body.classList.add("no-scroll");
  }

  enablePageScroll() {
    document.body.classList.remove("no-scroll");
  }

  disableScrollbarGutter() {
    document.documentElement.classList.add("no-scroll-gutter");
  }

  enableScrollbarGutter() {
    document.documentElement.classList.remove("no-scroll-gutter");
  }

  closeNav() {
    this.enablePageFocus();
    this.enablePageScroll();
    this.enableScrollbarGutter();
    window.removeEventListener("keydown", this.boundHandleEscapePress);
  }

  handlePageShow() {
    this.initializeBackState();
    this.updateBackButton();
  }

  goBack() {
    if (!this.shouldShowBackButton()) return;

    window.history.back();
  }

  updateBackButton() {
    if (!this.hasBackButtonTarget || !this.hasLogoTarget) return;

    const showBackButton = this.shouldShowBackButton();
    this.backButtonTarget.hidden = !showBackButton;
    this.logoTarget.hidden = showBackButton;
  }

  shouldShowBackButton() {
    return this.backButtonSurfaceEnabled() && this.canGoBackWithinApp();
  }

  backButtonSurfaceEnabled() {
    return document.body.classList.contains("hotwire-native-app") || this.mobileBackButtonMediaQuery.matches;
  }

  canGoBackWithinApp() {
    if (window.history.length <= 1) return false;

    return this.hasSeenInternalNavigation() || this.hasSameOriginReferrer();
  }

  hasSeenInternalNavigation() {
    try {
      return window.sessionStorage.getItem(this.backStateStorageKey()) === "true";
    } catch (_error) {
      return false;
    }
  }

  hasSameOriginReferrer() {
    if (!document.referrer) return false;

    try {
      return new URL(document.referrer).origin === window.location.origin;
    } catch (_error) {
      return false;
    }
  }

  markInternalNavigation(event) {
    if (!event.detail?.url) return;

    try {
      const nextUrl = new URL(event.detail.url, window.location.href);
      if (this.backStateResetPath(nextUrl)) {
        this.clearBackState();
        return;
      }

      if (nextUrl.origin === window.location.origin) {
        window.sessionStorage.setItem(this.backStateStorageKey(), "true");
      }
    } catch (_error) {
      // Ignore storage and URL parsing failures; the logo remains the fallback.
    }
  }

  initializeBackState() {
    const currentUrl = new URL(window.location.href);

    if (this.backStateResetPath(currentUrl)) {
      this.clearBackState();
      return;
    }

    if (window.bryghtNavigationBackStateInitialized) return;

    window.bryghtNavigationBackStateInitialized = true;

    if (this.arrivedFromOutsideApp()) this.clearBackState();
  }

  arrivedFromOutsideApp() {
    const navigationEntry = performance.getEntriesByType("navigation")[0];
    const fullPageNavigation = !navigationEntry || navigationEntry.type === "navigate";

    return fullPageNavigation && !this.hasSameOriginReferrer();
  }

  backStateResetPath(url) {
    if (url.origin !== window.location.origin) return true;

    // Keep this list in sync with BACK_BUTTON_EXCLUDED_PATH_PREFIXES in
    // app/helpers/native_app_helper.rb (the "/" entry is intentionally only here).
    return [
      "/",
      "/login",
      "/logout",
      "/native/session",
      "/quick_signup",
      "/passwords",
      "/session",
    ].some((path) => url.pathname === path || url.pathname.startsWith(`${path}/`));
  }

  clearBackState() {
    try {
      window.sessionStorage.removeItem(this.backStateStorageKey());
    } catch (_error) {
      // Ignore storage failures; the current render still falls back to the logo.
    }
  }

  backStateStorageKey() {
    return "bryght:internal-navigation-seen";
  }
}
